import { useCallback, useEffect, useRef } from 'react';
import { useLocationsStore } from './useLocationsStore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useRecentLocations } from './useRecentLocations';

/**
 * A custom React hook for handling location routing, user access to location and dashboard redirection
 * based on feature flag
 * @param {boolean} redirect - Whether to redirect the user to the appropriate path.
 */
function useLocationRouting(redirect: boolean = true): void {
  const ldClient = useLDClient();
  const navigateTo = useNavigate();
  const { locationId: URLLocationID } = useParams();
  const [searchParams] = useSearchParams();
  const allLocations = useLocationsStore(state => state.allLocations);
  const { removeLocation, recentLocations } = useRecentLocations();

  const processedLocationRef = useRef<string | null>(null);

  // Handle invalid location search param
  useEffect(() => {
    const invalidLocationParam = searchParams.get('invalidLocation');
    if (invalidLocationParam) {
      removeLocation(invalidLocationParam);
      navigateTo({ search: '' }, { replace: true });
    }
  }, [searchParams, removeLocation, navigateTo]);

  /**
   * Checks if the user has access to the given locationId
   */
  const userHasAccessToLocation = useCallback(
    (checkLocationId: string): string | undefined => {
      if (!allLocations || allLocations.length === 0) return;
      const location = allLocations.find(loc => loc.id === checkLocationId);
      return location?.id;
    },
    [allLocations]
  );

  /**
   * Finds the first available location from recent locations or all locations
   */
  const findFirstAvailableLocation = useCallback((): string | undefined => {
    if (recentLocations && recentLocations.length > 0) {
      const firstRecent = recentLocations[0];
      const hasAccess = userHasAccessToLocation(firstRecent.locationId);
      if (hasAccess) {
        return firstRecent.locationId;
      }
    }

    return allLocations?.length ? allLocations[0].id : undefined;
  }, [allLocations, recentLocations, userHasAccessToLocation]);

  /**
   * Finds the current locationId based on URLLocationID or stored locationId in localStorage
   */
  const findCurrentLocationId = useCallback((): string | undefined => {
    const sources = [URLLocationID, localStorage.getItem('hone:locationId')];

    for (const source of sources) {
      if (source) {
        const foundLocationId = userHasAccessToLocation(source);
        if (foundLocationId) {
          return foundLocationId;
        }
      }
    }

    return findFirstAvailableLocation();
  }, [URLLocationID, userHasAccessToLocation, findFirstAvailableLocation]);

  /**
   * Sets the current locationId in the location store, updates the localStorage, and navigates to the appropriate path.
   */
  const setCurrentLocation = useCallback(
    async (currentLocationId: string, shouldRedirect: boolean = false) => {
      if (processedLocationRef.current === currentLocationId) {
        return;
      }

      processedLocationRef.current = currentLocationId;
      useLocationsStore.setState({ currentLocationId });

      await ldClient?.identify({ key: currentLocationId });
      localStorage.setItem('hone:locationId', currentLocationId);

      if (shouldRedirect) {
        const newPath = `/app/location/${currentLocationId}/new-dashboard`;
        navigateTo(newPath);
      }
    },
    [ldClient, navigateTo]
  );

  // Main routing logic
  useEffect(() => {
    if (!allLocations || allLocations.length === 0) return;

    // Handle invalid location ID in URL
    if (URLLocationID && !userHasAccessToLocation(URLLocationID)) {
      const fallbackLocationId = findFirstAvailableLocation();
      if (fallbackLocationId) {
        const newPath = `/app/location/${fallbackLocationId}/new-dashboard?invalidLocation=${URLLocationID}`;
        processedLocationRef.current = fallbackLocationId;
        useLocationsStore.setState({ currentLocationId: fallbackLocationId });
        ldClient?.identify({ key: fallbackLocationId });
        localStorage.setItem('hone:locationId', fallbackLocationId);
        navigateTo(newPath);
      }
      return;
    }

    const currentLocationId = findCurrentLocationId();

    if (currentLocationId) {
      const shouldRedirect = redirect || URLLocationID !== currentLocationId;
      setCurrentLocation(currentLocationId, shouldRedirect);
    }
  }, [
    allLocations,
    URLLocationID,
    redirect,
    userHasAccessToLocation,
    findFirstAvailableLocation,
    findCurrentLocationId,
    setCurrentLocation,
    ldClient,
    navigateTo,
  ]);

  useEffect(() => {
    processedLocationRef.current = null;
  }, [URLLocationID]);
}

export default useLocationRouting;
